import React, { useState, useMemo, useCallback, useRef } from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { SearchField } from 'ui/search-field';

import Modal from 'components/partials/modal/modal';
import TableComponent from 'components/partials/table-component/table-component';
import type { TableAmountState } from 'components/partials/table-component/table-component';
import Button from 'components/partials/button/button';
import { Pagination } from 'components/partials/table-component/pagination/pagination';
import { ViewByAmountSelect } from 'components/partials/table-component/view-by-amount-select/view-by-amount-select';

import FundsFilters from './funds-filters';
import AppliedFiltersBar from './applied-filters-bar';
import { getSelectFundsModalTableSettings } from './select-funds-modal-table-settings';

import { useClientSpecialFunds } from '../../../hooks/use-aid-special-funds';

import { removeFundsFilter } from '../helpers/funds-filters.helpers';

import { EMPTY_FUNDS_FILTERS } from '../funds-step.types';
import type { FundsFilterValues } from '../funds-step.types';
import type { SortingState } from '@tanstack/react-table';

const PAGE_SIZE = 10;

interface FundItem {
  id: string | number;
  type?: string;
  name?: string;
  [key: string]: unknown;
}

interface SelectFundsModalProps {
  userId: string;
  clientId: string | number;
  modelId: string | number;
  excludeIds: (string | number)[];
  onAdd: (funds: FundItem[]) => void;
  onClose: () => void;
}

interface FundQueryFilters {
  id?: { notIn: string };
  type?: string[];
  source?: string[];
  instAid?: boolean;
  totalAid?: boolean;
  name?: { contains: string };
  code?: { equals: string };
}

const SelectFundsModal = ({
  userId,
  clientId,
  modelId,
  excludeIds,
  onAdd,
  onClose,
}: SelectFundsModalProps): React.JSX.Element => {
  const [search, setSearch] = useState<string>('');
  const [appliedFilters, setAppliedFilters] =
    useState<FundsFilterValues>(EMPTY_FUNDS_FILTERS);
  const [selectedFunds, setSelectedFunds] = useState<FundItem[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [sortingParams, setSortingParams] = useState<SortingState>([]);

  const filters = useMemo<FundQueryFilters>(() => {
    const f: FundQueryFilters = {};

    if (excludeIds.length > 0) {
      f.id = { notIn: excludeIds.join(',') };
    }

    if (appliedFilters.type.length > 0) {
      f.type = appliedFilters.type;
    }

    if (appliedFilters.source.length > 0) {
      f.source = appliedFilters.source;
    }

    if (appliedFilters.instAid !== null) {
      f.instAid = appliedFilters.instAid;
    }

    if (appliedFilters.totalAid !== null) {
      f.totalAid = appliedFilters.totalAid;
    }

    if (search) {
      if (/^\d+$/.test(search)) {
        f.code = { equals: search };
      } else {
        f.name = { contains: search };
      }
    }

    return f;
  }, [excludeIds, appliedFilters, search]);

  const queryResult = useClientSpecialFunds({
    userId,
    clientId,
    params: {
      filters,
      pageSize,
      pageIndex,
      modelId,
      sortBy: sortingParams,
    },
    enabled: true,
  }) as unknown as {
    data?: {
      data?: FundItem[];
      pagination?: { totalRecords?: number };
    };
    isLoading: boolean;
    isFetching: boolean;
  };
  const { data, isFetching } = queryResult;

  const pageData: FundItem[] = data?.data || [];
  const totalRecords = data?.pagination?.totalRecords || 0;
  const pagesAmount = Math.ceil(totalRecords / pageSize);

  const paginationAdapter = useMemo(
    () => ({
      getRowModel: () => ({
        rows: pageData.map((row) => ({ ...row, depth: 0 })),
      }),
      setPageIndex: (idx: number) => setPageIndex(idx),
      previousPage: () => setPageIndex((prev) => Math.max(0, prev - 1)),
      nextPage: () => setPageIndex((prev) => prev + 1),
      getPageCount: () => pagesAmount,
      setPageSize: (size: number) => {
        setPageSize(size);
        setPageIndex(0);
      },
    }),
    [pageData, pagesAmount]
  );

  const handleToggleFund = useCallback((fund: FundItem) => {
    setSelectedFunds((prev) =>
      prev.some((f) => f.id === fund.id)
        ? prev.filter((f) => f.id !== fund.id)
        : [...prev, fund]
    );
  }, []);

  const allSelected =
    pageData.length > 0 &&
    pageData.every((f) => selectedFunds.some((s) => s.id === f.id));

  const someSelected =
    pageData.some((f) => selectedFunds.some((s) => s.id === f.id)) &&
    !allSelected;

  // Latest-ref pattern: keeps handleToggleAll/columns stable across selection
  // changes so `columns` isn't rebuilt on every checkbox click (which forced
  // the table header, including the "select all" checkbox, to remount).
  const selectedFundsRef = useRef(selectedFunds);

  selectedFundsRef.current = selectedFunds;

  const pageDataRef = useRef(pageData);

  pageDataRef.current = pageData;

  const allSelectedRef = useRef(allSelected);

  allSelectedRef.current = allSelected;

  const someSelectedRef = useRef(someSelected);

  someSelectedRef.current = someSelected;

  const handleToggleAll = useCallback(() => {
    const currentPageData = pageDataRef.current;

    if (allSelectedRef.current) {
      const pageIds = new Set(currentPageData.map((f) => f.id));

      setSelectedFunds((prev) => prev.filter((f) => !pageIds.has(f.id)));
    } else {
      setSelectedFunds((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const newFunds = currentPageData.filter((f) => !existingIds.has(f.id));

        return [...prev, ...newFunds];
      });
    }
  }, []);

  const handleApplyFilters = (next: FundsFilterValues): void => {
    setAppliedFilters(next);
    setPageIndex(0);
  };

  const handleRemoveFilter = (
    field: keyof FundsFilterValues,
    value?: string
  ): void => {
    setAppliedFilters((prev) => removeFundsFilter(prev, field, value));
    setPageIndex(0);
  };

  const handleClearAllFilters = (): void => {
    setAppliedFilters(EMPTY_FUNDS_FILTERS);
    setPageIndex(0);
  };

  const handleSearchChange = (value: string): void => {
    setSearch(value);
    setPageIndex(0);
  };

  const handleTableSetAmount = useCallback(
    (updater: (state: TableAmountState) => TableAmountState): void => {
      const next = updater({ pageSize, totalRecords });

      setPageSize(next.pageSize);
      setPageIndex(0);
    },
    [pageSize, totalRecords]
  );

  const columns = useMemo(
    () =>
      getSelectFundsModalTableSettings({
        isFundSelected: (id) =>
          selectedFundsRef.current.some((f) => f.id === id),
        getAllSelected: () => allSelectedRef.current,
        getSomeSelected: () => someSelectedRef.current,
        onToggle: handleToggleFund,
        onToggleAll: handleToggleAll,
      }),
    [handleToggleFund, handleToggleAll]
  );

  const footer = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 2.5 }}>
      <Button
        type="button"
        variant="brand-filled"
        size="md"
        disabled={selectedFunds.length === 0}
        onClick={() => onAdd(selectedFunds)}
        data-testid="add-selected-button"
      >
        Add selected
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-modal-button"
      >
        Cancel
      </Button>
    </Stack>
  );

  return (
    <Modal
      headerText="Select funds to add"
      onCloseCallback={onClose}
      modalClassName="!w-[1150px] !max-w-[1150px] !max-h-[90vh] !overflow-visible"
      footer={footer}
      showCloseButton
      primaryAction={{
        onClick: () => onAdd(selectedFunds),
        disabled: selectedFunds.length === 0,
      }}
      data-testid="select-funds-modal"
    >
      <Stack sx={{ flex: '1 1 0%', minHeight: 0 }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 2.5, mt: 2, mb: 2, flexShrink: 0 }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 2, flex: '1 1 0%', minWidth: 0 }}
          >
            <SearchField
              onSearch={handleSearchChange}
              placeholder="Search..."
              sx={{ flex: 1 }}
            />
            <FundsFilters
              values={appliedFilters}
              onApply={handleApplyFilters}
            />
          </Stack>

          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 3, flexShrink: 0 }}
          >
            <Pagination
              table={paginationAdapter as never}
              pagesAmount={pagesAmount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
            />
            <ViewByAmountSelect
              table={paginationAdapter as never}
              pageSize={pageSize}
              setAmount={handleTableSetAmount}
            />
          </Stack>
        </Stack>

        <AppliedFiltersBar
          values={appliedFilters}
          onRemove={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
          sx={{ mb: 2 }}
        />

        <Box sx={{ flex: '1 1 0%', overflow: 'hidden', minHeight: 0 }}>
          <TableComponent
            columns={columns}
            data={pageData}
            isLoading={isFetching}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPagination={setPageIndex}
            setAmount={handleTableSetAmount}
            sortingParams={sortingParams}
            setSortingParams={(params) => {
              setSortingParams(params);
              setPageIndex(0);
            }}
            manualSorting
            totalRecords={totalRecords}
            pagination={false}
            customHeight="max-h-[50vh]"
          />
        </Box>

        {pageData.length === 0 && !isFetching && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'tokens.gray.500' }}>
            No funds available to add
          </Box>
        )}
      </Stack>
    </Modal>
  );
};

export default SelectFundsModal;
